import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api, apiError } from "@/lib/api";

const OPTIONS = [
  ["issues", "Issues", "Assignments, comments and closures on issues and NCRs"],
  ["tasks", "Tasks", "Tasks assigned to you in the Action board"],
  ["project", "Project", "Everything else: status alerts, shipments, weekly digests"],
];

/** Per-user email notification switches. */
export function NotificationSettings() {
  const [prefs, setPrefs] = useState(null);

  useEffect(() => {
    api
      .get("/me/notifications")
      .then((res) => setPrefs(res.data))
      .catch(() => setPrefs({ issues: true, tasks: true, project: true }));
  }, []);

  const toggle = async (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    try {
      const { data } = await api.put("/me/notifications", { [key]: value });
      setPrefs(data);
      toast.success(`${key} notifications ${value ? "on" : "off"}`);
    } catch (error) {
      setPrefs(prefs);
      toast.error(apiError(error));
    }
  };

  return (
    <section
      data-testid="notification-settings"
      className="mt-6 rounded-2xl border border-slate-200 bg-white p-6"
    >
      <div className="flex items-center gap-2">
        <Bell size={16} className="text-cyan-600" />
        <h2 className="font-display text-lg font-medium tracking-tight">Notification settings</h2>
      </div>
      <p className="mt-1.5 text-sm text-slate-600">
        Choose which emails you want to receive. Each switch works on its own.
      </p>

      <ul className="mt-4 divide-y divide-slate-100">
        {OPTIONS.map(([key, label, description]) => (
          <li key={key} className="flex items-center justify-between gap-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">{label}</p>
              <p className="text-[12px] text-slate-500">{description}</p>
            </div>
            <Switch
              data-testid={`notification-toggle-${key}`}
              checked={prefs ? prefs[key] !== false : true}
              disabled={!prefs}
              onCheckedChange={(value) => toggle(key, value)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
